/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable, EventEmitter } from '@angular/core';
import { AppService } from '../app.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { PushNotificationsService } from 'src/app/_services/push-notifications/logic/push-notifications.service';
import { AppDaoService } from '../../dao/app-dao.service';
import { Subject } from 'rxjs';

import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { TokenIdPage } from 'src/app/_pages/token-id/token-id.page';
import { ModalController } from '@ionic/angular';

import { Device, DeviceInfo } from '@capacitor/device';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { format } from 'date-fns';

/**
 * Loads Resources like Translation files
 */
@Injectable()
export class AppLogicService implements AppService {
  // Public Member
  public tokenEvent$ = new Subject<string>();

  // Private Member
  private version: Promise<string>;
  private buildNumber: Promise<string>;
  private packageName: Promise<string>;
  private tokenIdPromise: Promise<string>;
  private versionUpdate: Promise<boolean>;

  private deviceInfo: Promise<DeviceInfo>;

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private authenticationService: AuthenticationService,
    private appStorage: AppDaoService,
    private modalController: ModalController,
  ) {
    this.tokenEvent$.subscribe({
      next: (val) => {
        this.blockApplicationWithoutTokenId();
      },
      error: (error) => {
        this.blockApplicationWithoutTokenId();
      },
    });
    this.authenticationService.authenticationState.subscribe({
      next: (val) => {
        this.blockApplicationWithoutTokenId();
      },
    });

    this.version = new Promise<string>((resolve, reject) => {
      if (Capacitor.isNativePlatform()) {
      App.getInfo()
        .then((it) => resolve(it.version))
        .catch((error) => {
          this.logger.error(
            this.constructor.name,
            '- Could not retrieve Appversion',
            error?.message,
          );
          resolve(undefined);
        });
      } else {
        resolve(environment.version)
      }
    });

    this.buildNumber = new Promise<string>((resolve, reject) => {
      if (Capacitor.isNativePlatform()) {
        App.getInfo()
          .then((it) => {
            this.checkForAppUpdate(it.build);
            resolve(it.build)
          })
          .catch((error) => {
            this.logger.error(
              this.constructor.name,
              '- Could not retrieve Buildnumber',
              error?.message,
            );
            resolve(undefined);
          });
      } else {
        resolve(environment.buildnumber)
      }
    });

    this.deviceInfo = new Promise<DeviceInfo>((resolve, reject) => {
      Device.getInfo()
        .then((info) => {
          resolve(info);
        })
        .catch((error) => {
          this.logger.error(
            this.constructor.name,
            '- Could not retrieve Device Info',
            error?.message,
          );
          reject(error);
        });
    });

    this.packageName = new Promise((resolve, reject) => {
      if (Capacitor.isNativePlatform()) {
        App.getInfo()
          .then((it) => resolve(it.id))
          .catch((error) => {
            this.logger.error(
              this.constructor.name,
              '- Could not retrieve Package Name',
              error?.message,
            );
            resolve(undefined);
          });
        } else {
          resolve('com.lbslogics.imspwa')
        }
    });

    this.tokenIdPromise = appStorage.loadTokenId();
    this.tokenIdPromise
      .then((val) => {
        this.tokenEvent$.next(val);
      })
      .catch(() => {
        this.tokenEvent$.next(undefined);
      });
  }

  // AppService
  public getAppVersion(): Promise<string> {
    return this.version;
  }

  public getBuildNumber(): Promise<string> {
    return this.buildNumber;
  }

  public getDeviceInfo(): Promise<DeviceInfo> {
    return this.deviceInfo;
  }

  public getPackageName(): Promise<string> {
    return this.packageName;
  }

  public getTokenId(): Promise<string> {
    return this.tokenIdPromise;
  }

  public async updateTokenId(tokenId: string): Promise<void> {
    await this.appStorage.saveTokenId(tokenId);
    this.tokenIdPromise = Promise.resolve(tokenId);
    this.tokenIdPromise
      .then((val) => {
        this.tokenEvent$.next(val);
      })
      .catch(() => {
        this.tokenEvent$.next(undefined);
      });
  }

  public isVersionUpdate(): Promise<boolean> {
    return this.versionUpdate;
  }

  // Resetable

  public async reset() {
    await this.appStorage.reset();
  }

  public async clear() {
    await this.appStorage.clear();
    this.tokenIdPromise = Promise.resolve(undefined);
    this.tokenEvent$.next(undefined);
  }

  // Private Functions
  private async blockApplicationWithoutTokenId() {
    let authenticated =
      this.authenticationService.authenticationState.getValue() || false;
    this.logger.log(
      this.constructor.name,
      '- blockApplicationWithoutTokenId - user is authenticated:',
      authenticated,
    );

    this.logger.log(this.constructor.name, 'wait for tokenId Promise');
    let tokenId = await this.tokenIdPromise;
    this.logger.log(this.constructor.name, 'got tokenId Promise', tokenId);

    if (authenticated && (tokenId === undefined || tokenId === null)) {
      this.logger.log(
        this.constructor.name,
        '- blockApplicationWithoutTokenId - block application',
      );
      const modal = await this.modalController.create({
        component: TokenIdPage,
        backdropDismiss: false,
        cssClass: 'fullscreen-modal',
      });
      await modal.present();
    } else {
      this.logger.log(
        this.constructor.name,
        '- blockApplicationWithoutTokenId - no need to block user',
      );
    }
  }

  private async checkForAppUpdate(versionCode: string | number) {
    try {
      let oldBuildVersion = await this.appStorage.loadBuildVersion();

      this.logger.log(
        this.constructor.name,
        'Check for App Update old',
        oldBuildVersion,
        'new:',
        versionCode,
      );
      if (oldBuildVersion == undefined || oldBuildVersion < versionCode) {
        this.logger.log(
          this.constructor.name,
          'App Update detected. Auto Sync data:',
          oldBuildVersion,
          versionCode,
        );

        await this.appStorage.saveBuildVersion(versionCode);
        await this.authenticationService.finishedSetup();
        if (!this.authenticationService.authenticationState.getValue()) {
          this.logger.log(
            this.constructor.name,
            '- abort App Update because user is not authenticated',
          );
          return;
        }
        this.versionUpdate = Promise.resolve(true);
      } else if (oldBuildVersion > versionCode) {
        this.logger.log(
          this.constructor.name,
          'App Update: App Downgrade detected - reset version number to enable auto update on next update.',
        );
        await this.appStorage.saveBuildVersion(versionCode);
      } else {
        this.logger.log(
          this.constructor.name,
          'No App Update detected.',
          oldBuildVersion,
          versionCode,
        );
        this.versionUpdate = Promise.resolve(false);
      }
    } catch (error) {
      this.logger.warn(
        this.constructor.name,
        'Could not check for App Update - storage does not return an old version',
      );
    }
  }
}
